import type { ExternalConnection } from '../../../lib/external-agents/types';
import type {
  ManagedAgentImport,
  ManagedAgentList,
  WorkspaceKeyStatus,
} from '../../../lib/managed-agents/types';
import type { StatusTone } from './status-pill';

export interface ConnectedAgentStatus {
  tone: StatusTone;
  label: string;
  /** One line on what the status means for the user. */
  detail?: string;
  /** A newer version the agent can move to. */
  newerVersion?: number;
  /** Never seen answering: offer a "Say hello" test. */
  needsTest?: boolean;
  canRun: boolean;
}

/** What an imported agent's row should say, from what we know right now. */
export function getConnectedAgentStatus(
  agentImport: ManagedAgentImport | undefined,
  keyStatus: WorkspaceKeyStatus | null,
  remote: ManagedAgentList | null,
): ConnectedAgentStatus {
  if (!agentImport) {
    return {
      tone: 'bad',
      label: 'Import settings missing',
      detail: 'Remove it and import the agent again.',
      canRun: false,
    };
  }
  if (keyStatus && !keyStatus.connected) {
    return {
      tone: 'bad',
      label: 'Key missing',
      detail: 'Add an Anthropic API key in the API keys tab.',
      canRun: false,
    };
  }
  if (!remote) return { tone: 'neutral', label: 'Checking…', canRun: true };

  const current = remote.agents.find(
    (agent) => agent.id === agentImport.anthropic_agent_id,
  );
  if (!current) {
    return {
      tone: 'bad',
      label: 'Not found in Claude Console',
      detail: 'It was deleted, or this key cannot see it.',
      canRun: false,
    };
  }
  if (current.archivedAt) {
    return {
      tone: 'bad',
      label: 'Archived in Claude Console',
      detail: "New simulations can't start. Past results stay.",
      canRun: false,
    };
  }
  if (current.version > agentImport.anthropic_agent_version) {
    return {
      tone: 'warn',
      label: `v${current.version} available`,
      detail: `Simulations keep using v${agentImport.anthropic_agent_version} until you update.`,
      newerVersion: current.version,
      canRun: true,
    };
  }
  if (!agentImport.last_verified_at) {
    return {
      tone: 'neutral',
      label: 'Not tested',
      detail: 'Test it once, or run a simulation, to confirm it works.',
      needsTest: true,
      canRun: true,
    };
  }
  return { tone: 'ok', label: 'Ready', canRun: true };
}

/** An agent behind the customer's own API is ready once a test has passed. */
export function getEndpointAgentStatus(
  connection: ExternalConnection | undefined,
): ConnectedAgentStatus {
  if (!connection) {
    return {
      tone: 'bad',
      label: 'Settings missing',
      detail: 'Remove it and connect the endpoint again.',
      canRun: false,
    };
  }
  if (!connection.last_test_passed_at) {
    return {
      tone: 'warn',
      label: 'Needs a test',
      detail: 'Simulations start once a connection test passes.',
      needsTest: true,
      canRun: false,
    };
  }
  return { tone: 'ok', label: 'Ready', canRun: true };
}
